import {inject} from '@angular/core';
import {HttpErrorResponse, HttpInterceptorFn} from '@angular/common/http';
import {catchError, switchMap, throwError} from 'rxjs';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {AuthValidationService} from './auth-validation.service';

/**
 * Auth endpoints that must never be retried. The refresh flow calls these
 * itself, so retrying them would loop.
 */
const EXCLUDED_AUTH_PATHS = ['/auth/refresh', '/auth/validate-token', '/auth/logout', '/auth/sso-inbound'];

/**
 * Recovers from an expired access token mid-session.
 *
 * The access token is short-lived and authGuard only refreshes it when entering a guarded
 * route, so after it expires every @RequireJwt call (e.g. the product block quiz stats)
 * would 401 until a full page reload. On a 401 from the Tuatara API for a request that
 * carried a bearer token, this refreshes the token via the HttpOnly cookie and retries
 * the request once with the new token.
 */
export const authRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const environment = inject(ENV_TOKEN) as EnvInterface;
  const authValidationService = inject(AuthValidationService);

  const apiBase = environment.security.tuatara.endpoint;
  const isApiRequest = !!apiBase && req.url.startsWith(apiBase);
  const hasBearer = req.headers.get('Authorization')?.startsWith('Bearer ') ?? false;
  const isExcluded = EXCLUDED_AUTH_PATHS.some(path => req.url.includes(path));

  if (!isApiRequest || !hasBearer || isExcluded) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
        return throwError(() => error);
      }
      return authValidationService.refreshAccessToken().pipe(
        switchMap(newToken => {
          if (!newToken) {
            return throwError(() => error);
          }
          return next(req.clone({setHeaders: {Authorization: `Bearer ${newToken}`}}));
        })
      );
    })
  );
};
